/**
 * VerdictBlock — the tutor's one-line verdict on the attempt: an honest,
 * warm-but-truthful summary sentence emitted by FreeformRubricV1 (whole
 * attempt) and StructuredAssessmentV1 (whole multi-part question) as
 * `llmAssessment.verdict`. Rendered as the lead of the solution-analysis body,
 * above the step/part detail. Stateless atom; renders nothing when empty.
 *
 * A descriptive answer (DescriptiveAnswerV1) adds an `assessment` — what was
 * right and what was missing — which reads as the rest of the summary, so it
 * sits under the verdict inside the same section rather than as a block of
 * its own after it.
 * See docs/FeatureDesigns/diagnosis/tutor-feedback-model.md.
 */
import { LatexText } from '../../latex';

interface Props {
    verdict: string | undefined;
    /** The assessment proper, under the verdict. Shown only with a verdict. */
    assessment?: string;
    /** `'lead'` drops the "Analysis summary" heading and sets the sentence in
     *  body-lead type. A heading over a single sentence that is already the
     *  summary is one more thing to read past, so the focused student view
     *  lets the sentence speak for itself. */
    variant?: 'section' | 'lead';
}

export function VerdictBlock({ verdict, assessment, variant = 'section' }: Props) {
    if (!verdict || !verdict.trim()) return null;
    const body = assessment?.trim();
    return (
        <section className="sol-section sol-verdict">
            {variant === 'section' && <h3 className="sol-section-title">Analysis summary</h3>}
            <p className={`sol-verdict-text${variant === 'lead' ? ' sol-verdict-text--lead' : ''}`}>
                <LatexText text={verdict} />
            </p>
            {body && (
                <p className="sol-assessment-text"><LatexText text={body} /></p>
            )}
        </section>
    );
}
